import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Settings,
  BarChart3,
  Zap,
  ShieldCheck,
  FileText,
  Users,
  ShoppingCart,
  Box,
  Factory,
  UserCheck,
  Search,
  SquarePen,
  LifeBuoy,
  BriefcaseMedical,
  Car,
  Briefcase,
} from "lucide-react";
import StackNavbar from "../components/StackNavbar";
import Footer from "../components/Footer";
import { openAgent } from "../utils/openAgent";

const SIMPEL_ERP_FEATURES = [
  {
    title: "Unified Business Management",
    desc: "Manage HR, Finance, Sales, Procurement, Inventory and more in a single platform.",
    icon: Settings,
    iconColor: "#0284C7",
    iconBg: "#E0F2FE",
  },
  {
    title: "Built for Growth",
    desc: "Modular and customizable to meet the needs of startups, SMEs and enterprises.",
    icon: BarChart3,
    iconColor: "#A855F7",
    iconBg: "#FAE8FF",
  },
  {
    title: "Smart Automation & Insights",
    desc: "Automate routine processes and gain real-time insights with AI-powered analytics.",
    icon: Zap,
    iconColor: "#16A34A",
    iconBg: "#DCFCE7",
  },
  {
    title: "Secure & Reliable",
    desc: "Cloud-based with enterprise-grade security, ensuring your data is always safe.",
    icon: ShieldCheck,
    iconColor: "#EA580C",
    iconBg: "#FEF3EB",
  },
];

const SIMPEL_ERP_CAPABILITIES = [
  {
    title: "Finance Management",
    desc: "manage accounting, invoicing, transactions and compliance.",
    icon: FileText,
    iconColor: "#2563EB",
    iconBg: "#EBF3FF",
  },
  {
    title: "Sales & CRM",
    desc: "Track leads, manage orders and strengthen customer relationships.",
    icon: Users,
    iconColor: "#EA580C",
    iconBg: "#FEF3EB",
  },
  {
    title: "Procurement",
    desc: "Streamline purchasing, vendor management and approvals.",
    icon: ShoppingCart,
    iconColor: "#16A34A",
    iconBg: "#DCFCE7",
  },
  {
    title: "Inventory Management",
    desc: "Real-time stock tracking, warehouse management and demand planning.",
    icon: Box,
    iconColor: "#9333EA",
    iconBg: "#F3E8FF",
  },
  {
    title: "Manufacturing",
    desc: "Plan production, manage BOM, track work orders and improve efficiency.",
    icon: Factory,
    iconColor: "#D946EF",
    iconBg: "#FDF4FF",
  },
  {
    title: "Human Resources",
    desc: "Manage employees, attendance, payroll and performance.",
    icon: UserCheck,
    iconColor: "#65A30D",
    iconBg: "#F7FEE7",
  },
];

const SIMPEL_ERP_IMPLEMENTATION_STEPS = [
  {
    title: "Understand",
    desc: "Analyze your business needs and current processes.",
    icon: Search,
    iconColor: "#0284C7",
    iconBg: "#CFF0FE",
    glowColor: "rgba(0, 210, 238, 0.55)",
  },
  {
    title: "Configure",
    desc: "Set up and customize the platform for your requirements.",
    icon: SquarePen,
    iconColor: "#A855F7",
    iconBg: "#F5D0FE",
    glowColor: "rgba(217, 70, 239, 0.5)",
  },
  {
    title: "Deploy",
    desc: "Migrate your data and go live with minimal disruption.",
    icon: Box,
    iconColor: "#CA8A04",
    iconBg: "#FEF9C3",
    glowColor: "rgba(234, 179, 8, 0.5)",
  },
  {
    title: "Train & Enable",
    desc: "Empower your team with targeted training and support.",
    icon: LifeBuoy,
    iconColor: "#16A34A",
    iconBg: "#DCFCE7",
    glowColor: "rgba(34, 197, 94, 0.5)",
  },
  {
    title: "Grow",
    desc: "Monitor performance and continuously optimize.",
    icon: BarChart3,
    iconColor: "#E11D48",
    iconBg: "#FFE4E6",
    glowColor: "rgba(244, 63, 94, 0.5)",
  },
];

const SIMPEL_ERP_INDUSTRIES = [
  {
    title: "Manufacturing",
    desc: "Production planning, inventory and supply chain management.",
    icon: Factory,
    iconColor: "#0891B2",
    iconBg: "#CFFAFE",
    borderColor: "#CFFAFE",
  },
  {
    title: "Retail & Distribution",
    desc: "Inventory, order management and multi-channel operations.",
    icon: ShoppingCart,
    iconColor: "#2563EB",
    iconBg: "#DBEAFE",
    borderColor: "#DBEAFE",
  },
  {
    title: "Automotive",
    desc: "End-to-end operations and supplier collaboration.",
    icon: Car,
    iconColor: "#9333EA",
    iconBg: "#F3E8FF",
    borderColor: "#F3E8FF",
  },
  {
    title: "Healthcare",
    desc: "Compliance, inventory and patient management.",
    icon: BriefcaseMedical,
    iconColor: "#16A34A",
    iconBg: "#DCFCE7",
    borderColor: "#DCFCE7",
  },
  {
    title: "Professional Services",
    desc: "Project management, resource planning and billing.",
    icon: Briefcase,
    iconColor: "#EA580C",
    iconBg: "#FFEDD5",
    borderColor: "#FFEDD5",
  },
];

const SimpelErpPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen w-full bg-[#050C17] text-white flex flex-col selection:bg-[#00D2EE] selection:text-black overflow-x-hidden">
      {/* Top Navigation */}
      <StackNavbar />

      <main className="flex-1">
        {/* =========================================================================
            SECTION 1: HERO SECTION: SIMPEL ERP (Matching Screenshot Exactly)
            Dimensions: 1440px x 617px
           ========================================================================= */}
        <section
          className="relative w-full overflow-hidden"
          style={{
            minHeight: "617px",
            background: "linear-gradient(115deg, #091D38 0%, #0C284C 35%, #0F3364 70%, #103B75 100%)",
          }}
        >
          {/* Subtle Ambient Glows matching screenshot depth */}
          <div className="absolute right-[5%] top-1/2 -translate-y-1/2 w-[600px] h-[450px] bg-[#0075FF]/20 blur-[130px] rounded-full pointer-events-none z-0" />
          <div className="absolute left-[10%] top-[25%] w-[400px] h-[300px] bg-[#00BAEB]/10 blur-[120px] rounded-full pointer-events-none z-0" />

          {/* Main Hero Content (Left text, Right Laptop mockup) */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] min-h-[617px] flex items-center px-6 sm:px-10 lg:px-12 xl:px-16 pt-[84px] sm:pt-[97px] pb-12 lg:pb-0">
            <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-10 xl:gap-14">
              {/* Left Column: Breadcrumbs, Title, Subtitle, CTA Button */}
              <div className="w-full lg:w-auto shrink-0 z-20 max-w-[540px] xl:max-w-[580px] flex flex-col items-start">
                {/* Breadcrumbs / Eyebrow */}
                <div className="flex items-center gap-2 text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#7E98B8] select-none">
                  <Link
                    to="/what-we-do"
                    className="hover:text-white transition-colors duration-200"
                  >
                    WHAT WE DO
                  </Link>
                  <span className="text-[#4E6888]">&gt;</span>
                  <Link
                    to="/what-we-do#products"
                    className="hover:text-white transition-colors duration-200"
                  >
                    PRODUCTS
                  </Link>
                  <span className="text-[#4E6888]">&gt;</span>
                  <span className="text-[#9DB5D0]">SIMPELERP</span>
                </div>

                {/* Main Headline */}
                <h1 className="mt-4 sm:mt-5 text-[32px] sm:text-[38px] lg:text-[40px] xl:text-[48px] 2xl:text-[54px] font-extrabold leading-[1.08] tracking-[-0.025em] text-white">
                  <span className="block">SimpelERP</span>
                  <span className="block text-[#00BAEB] whitespace-nowrap">
                    Simplify. Streamline. Scale.
                  </span>
                </h1>

                {/* Subtitle / Paragraph Description */}
                <p className="mt-5 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[25px] text-[#8EA3BD] max-w-[480px]">
                  A cloud-based, AI-ready ERP platform that adapts to your business
                  — streamlining operations for startups, SMEs, and enterprises.
                </p>

                {/* CTA Button: Schedule a Demo */}
                <div className="mt-8 sm:mt-9">
                  <button
                    onClick={openAgent}
                    className="group inline-flex items-center gap-2.5 rounded-full bg-white px-7 sm:px-8 py-3.5 text-[14.5px] sm:text-[15px] font-bold text-[#06152B] shadow-[0_0_24px_rgba(0,186,235,0.45)] hover:shadow-[0_0_36px_rgba(0,186,235,0.75)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer"
                  >
                    <span>Schedule a Demo</span>
                    <ArrowRight
                      className="h-4 w-4 text-[#06152B] transition-transform duration-200 group-hover:translate-x-1"
                      strokeWidth={2.4}
                    />
                  </button>
                </div>
              </div>

              {/* Right Column: Laptop Mockup Displaying SimpelERP - Flushed to Right Edge with Guaranteed Gap */}
              <div className="relative w-full lg:w-[46%] xl:w-[48%] flex items-center justify-center lg:justify-end z-10 shrink-0 lg:-mr-4 xl:mr-0">
                <div className="relative w-full max-w-[450px] sm:max-w-[500px] lg:max-w-[520px] xl:max-w-[640px] 2xl:max-w-[700px]">
                  <img
                    src="/images/products/simpel-erp-laptop.png"
                    alt="SimpelERP Software Interface on Laptop"
                    className="w-full h-auto object-contain select-none pointer-events-none drop-shadow-[0_25px_60px_rgba(0,0,0,0.55)]"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 2: BUILT FOR MODERN BUSINESSES (A Smarter Way to Manage Your Business.)
            Background: rgba(255, 255, 255, 1)
           ========================================================================= */}
        <section
          className="relative w-full py-20 lg:py-24 text-[#06152B]"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-12 xl:px-16">
            <div className="flex flex-col lg:flex-row items-start justify-between gap-12 lg:gap-14 xl:gap-16">
              {/* Left Column: Eyebrow, Headline & Description */}
              <div className="max-w-[460px] shrink-0 flex flex-col items-start">
                <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#51627C]">
                  BUILT FOR MODERN BUSINESSES
                </p>

                <h2 className="mt-3.5 text-[32px] sm:text-[38px] lg:text-[42px] font-extrabold leading-[1.14] tracking-tight text-[#06152B]">
                  <span className="block">A Smarter Way to</span>
                  <span className="block whitespace-nowrap">
                    <span className="text-[#00D2EE]">Manage </span>
                    <span className="text-[#0075FF]">Your Business.</span>
                  </span>
                </h2>

                <p className="mt-5 text-[14px] sm:text-[14.5px] leading-[24px] sm:leading-[25px] text-[#51627C] max-w-[390px]">
                  SimpelERP is a cloud-based, AI-ready ERP solution by<br className="hidden sm:inline" />{" "}
                  Simpel Techlabs Pvt Ltd, designed to unify your<br className="hidden sm:inline" />{" "}
                  people, processes and data. It helps businesses<br className="hidden sm:inline" />{" "}
                  streamline operations, improve visibility and make<br className="hidden sm:inline" />{" "}
                  smarter decisions — all from one platform.
                </p>
              </div>

              {/* Right Column: 4 Feature Cards (2x2 Grid) */}
              <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
                {SIMPEL_ERP_FEATURES.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="rounded-[20px] p-6 sm:p-7 flex flex-col justify-start text-left bg-white border border-[#E2E8F0] shadow-[0_4px_20px_rgba(0,0,0,0.03)] select-none"
                    >
                      {/* Circular Icon Badge */}
                      <div
                        className="w-[46px] h-[46px] rounded-full flex items-center justify-center shrink-0"
                        style={{ backgroundColor: item.iconBg }}
                      >
                        <Icon
                          className="w-5 h-5"
                          style={{ color: item.iconColor }}
                          strokeWidth={2.2}
                        />
                      </div>

                      {/* Title */}
                      <h3 className="mt-5 text-[17px] sm:text-[18px] font-bold text-[#06152B] tracking-tight leading-snug">
                        {item.title}
                      </h3>

                      {/* Description */}
                      <p className="mt-2 text-[13px] sm:text-[13.5px] leading-[20px] text-[#51627C]">
                        {item.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 3: PRODUCT CAPABILITIES (Everything you need in One Platform.)
            Background: rgba(236, 245, 253, 1)
           ========================================================================= */}
        <section
          className="relative w-full py-20 lg:py-24 text-[#06152B]"
          style={{ backgroundColor: "rgba(236, 245, 253, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-12 xl:px-16">
            {/* Header Block: Left title + Right bordered description */}
            <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
              <div className="max-w-[620px]">
                <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#51627C]">
                  PRODUCT CAPABILITIES
                </p>
                <h2 className="mt-3.5 text-[32px] sm:text-[40px] lg:text-[44px] font-extrabold leading-[1.12] tracking-tight text-[#06152B]">
                  <span className="block">Everything you need</span>
                  <span className="block">
                    in <span className="text-[#00BAEB]">One Platform.</span>
                  </span>
                </h2>
              </div>

              {/* Right Description with vertical separator bar */}
              <div className="max-w-[460px] border-l-2 border-[#CBD5E1] pl-5 sm:pl-6 py-1">
                <p className="text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[24px] text-[#51627C]">
                  Experience measurable improvements across every aspect of your operations.
                </p>
              </div>
            </div>

            {/* 6 Capability Cards Grid (2 rows of 3) */}
            <div className="mt-12 sm:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {SIMPEL_ERP_CAPABILITIES.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className="rounded-[20px] p-6 sm:p-7 flex flex-col justify-start text-left bg-white border border-[#E2E8F0] shadow-[0_2px_12px_rgba(0,0,0,0.03)] select-none min-h-[215px]"
                  >
                    {/* Circular Icon Badge */}
                    <div
                      className="w-[46px] h-[46px] rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: item.iconBg }}
                    >
                      <Icon
                        className="w-5 h-5"
                        style={{ color: item.iconColor }}
                        strokeWidth={2.2}
                      />
                    </div>

                    {/* Title */}
                    <h3 className="mt-6 text-[17px] sm:text-[18px] font-bold text-[#06152B] tracking-tight leading-snug">
                      {item.title}
                    </h3>

                    {/* Description */}
                    <p className="mt-2 text-[13px] sm:text-[13.5px] leading-[20px] text-[#51627C]">
                      {item.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 4: A PROVEN IMPLEMENTATION APPROACH (From setup to Business Impact.)
            Background Details from Figma Inspect:
            Width: 1,440px | Height: 496px | Border Top: 1px rgba(30, 41, 59, 1)
            Background Color: rgba(5, 10, 21, 1) | Background Graphic: simpel-erp-journey-bg.png
           ========================================================================= */}
        <section
          className="relative w-full overflow-hidden py-16 sm:py-20 lg:py-24"
          style={{
            backgroundColor: "rgba(5, 10, 21, 1)",
            borderTop: "1px solid rgba(30, 41, 59, 1)",
            minHeight: "496px",
          }}
        >
          {/* Futuristic Blue Glow Background Graphic provided by user */}
          <img
            src="/images/products/simpel-erp-journey-bg.png"
            alt="Implementation Journey Background"
            className="absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none opacity-85"
          />

          {/* Soft Dark Vignette for contrast */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(5, 10, 21, 0.65) 0%, rgba(5, 10, 21, 0.25) 50%, rgba(5, 10, 21, 0.75) 100%)",
            }}
          />

          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-12 xl:px-16">
            {/* Header Block: Left-aligned Eyebrow, Title & Description */}
            <div className="max-w-[700px] flex flex-col items-start">
              <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#7E98B8]">
                A PROVEN IMPLEMENTATION APPROCH
              </p>
              <h2 className="mt-3.5 text-[30px] sm:text-[38px] lg:text-[44px] font-extrabold leading-[1.14] tracking-tight text-white">
                From setup to Business Impact.
              </h2>
              <p className="mt-3.5 text-[13.5px] sm:text-[14.5px] leading-[22px] sm:leading-[24px] text-[#8EA3BD] max-w-[620px]">
                Get up and running quickly with a structured implementation process designed for your business.
              </p>
            </div>

            {/* 5 Steps Timeline Flow */}
            <div className="mt-14 sm:mt-16 lg:mt-20 flex flex-col lg:flex-row items-center lg:items-start justify-between gap-8 lg:gap-2 xl:gap-4">
              {SIMPEL_ERP_IMPLEMENTATION_STEPS.map((item, idx) => {
                const Icon = item.icon;
                const isLast = idx === SIMPEL_ERP_IMPLEMENTATION_STEPS.length - 1;
                return (
                  <React.Fragment key={item.title}>
                    {/* Step Item */}
                    <div className="flex flex-col items-center text-center max-w-[200px] w-full group select-none">
                      {/* Glowing Circle Icon Badge */}
                      <div
                        className="w-[66px] h-[66px] sm:w-[70px] sm:h-[70px] rounded-full flex items-center justify-center transition-all duration-200"
                        style={{
                          backgroundColor: item.iconBg,
                          boxShadow: `0 0 32px ${item.glowColor}`,
                        }}
                      >
                        <Icon
                          className="w-7 h-7"
                          style={{ color: item.iconColor }}
                          strokeWidth={2.4}
                        />
                      </div>

                      {/* Step Title */}
                      <h3 className="mt-5 text-[18px] sm:text-[19px] font-bold text-white tracking-tight leading-snug">
                        {item.title}
                      </h3>

                      {/* Step Description */}
                      <p className="mt-2 text-[12.5px] sm:text-[13px] leading-[20px] text-[#8EA3BD]">
                        {item.desc}
                      </p>
                    </div>

                    {/* Connecting Arrow between steps */}
                    {!isLast && (
                      <div className="hidden lg:flex items-center justify-center shrink-0 mt-[23px] text-[#00D2EE]">
                        <ArrowRight
                          className="w-6 h-6 text-[#00D2EE]"
                          strokeWidth={2.4}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 5: SEAMLESS INTEGRATIONS (Connect across your ecosystem.)
            Background: rgba(236, 245, 253, 1)
           ========================================================================= */}
        <section
          className="relative w-full py-16 sm:py-20 lg:py-24 text-[#06152B]"
          style={{ backgroundColor: "rgba(236, 245, 253, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-12 xl:px-16">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-12 xl:gap-16">
              {/* Left Column: Eyebrow, Title & Description */}
              <div className="w-full lg:w-auto max-w-[440px] xl:max-w-[460px] shrink-0 flex flex-col items-start">
                <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#51627C]">
                  SEAMLESS INTEGRATIONS
                </p>

                <h2 className="mt-3.5 text-[32px] sm:text-[38px] lg:text-[44px] font-extrabold leading-[1.12] tracking-tight text-[#06152B]">
                  <span className="block">Connect across your</span>
                  <span className="block text-[#00BAEB]">ecosystem.</span>
                </h2>

                <p className="mt-5 text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[24px] text-[#51627C]">
                  Whether you are a student, working professional or robotics
                  enthusiast, XP Skills is designed for anyone who wants to
                  build real skills in robotics and Physical AI.
                </p>
              </div>

              {/* Right Column: Integrations Diagram Illustration */}
              <div className="relative w-full flex-1 flex items-center justify-center lg:justify-end">
                <div className="relative w-full max-w-[620px] lg:max-w-[720px] xl:max-w-[760px]">
                  <img
                    src="/images/products/simpel-erp-integrations.png"
                    alt="SimpelERP Connect across your ecosystem integrations diagram"
                    className="w-full h-auto object-contain select-none pointer-events-none drop-shadow-[0_4px_24px_rgba(37,99,235,0.06)]"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 6: DESIGNED FOR EVERY INDUSTRY (Trusted by Businesses Across Industries.)
            Background: rgba(255, 255, 255, 1)
           ========================================================================= */}
        <section
          className="relative w-full py-16 sm:py-20 lg:py-24 text-[#06152B]"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-12 xl:px-16">
            {/* Header Block: Left title + Right bordered description */}
            <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
              <div className="max-w-[620px]">
                <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#51627C]">
                  DESIGNED FOR EVERY INDUSTRY
                </p>
                <h2 className="mt-3.5 text-[30px] sm:text-[38px] lg:text-[44px] font-extrabold leading-[1.14] tracking-tight text-[#06152B]">
                  <span className="block">Trusted by Businesses</span>
                  <span className="block">Across Industries.</span>
                </h2>
              </div>

              {/* Right Description with vertical separator bar */}
              <div className="max-w-[460px] border-l-2 border-[#CBD5E1] pl-5 sm:pl-6 py-1">
                <p className="text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[24px] text-[#51627C]">
                  SimpelERP adapts to your industry needs, helping you
                  streamline operations and achieve measurable results.
                </p>
              </div>
            </div>

            {/* 5 Industry Cards (Horizontal Row) */}
            <div className="mt-12 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
              {SIMPEL_ERP_INDUSTRIES.map((card) => {
                const Icon = card.icon;
                return (
                  <div
                    key={card.title}
                    className="rounded-[20px] p-6 sm:p-6.5 flex flex-col items-center text-center justify-start bg-white shadow-[0_2px_12px_rgba(0,0,0,0.02)] select-none min-h-[225px]"
                    style={{ border: `1.5px solid ${card.borderColor}` }}
                  >
                    {/* Circular Icon Badge */}
                    <div
                      className="w-[46px] h-[46px] rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: card.iconBg }}
                    >
                      <Icon
                        className="w-5 h-5"
                        style={{ color: card.iconColor }}
                        strokeWidth={2.2}
                      />
                    </div>

                    {/* Title */}
                    <h3 className="mt-5 text-[16.5px] sm:text-[17px] font-bold text-[#06152B] tracking-tight leading-snug">
                      {card.title}
                    </h3>

                    {/* Description */}
                    <p className="mt-2.5 text-[12.5px] sm:text-[13px] leading-[19px] text-[#51627C]">
                      {card.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 7: CTA BANNER (Let’s Build a Smarter Tomorrow.)
            Background Details from Figma Inspect:
            Width: 1,440px | Height: 431px
            Linear Gradient: rgba(4, 16, 29, 1) -> rgba(7, 22, 38, 0) -> rgba(8, 42, 89, 0)
            Background Graphic: simpel-erp-cta-bg.jpg
           ========================================================================= */}
        <section
          className="relative w-full overflow-hidden flex items-center py-16 sm:py-20 lg:py-0"
          style={{
            backgroundColor: "rgba(4, 16, 29, 1)",
            minHeight: "431px",
          }}
        >
          {/* Background Image provided by user */}
          <img
            src="/images/products/simpel-erp-cta-bg.jpg"
            alt="Let's Build a Smarter Tomorrow"
            className="absolute inset-0 h-full w-full object-cover object-right sm:object-center select-none pointer-events-none z-0"
          />

          {/* Left-to-Right gradient overlay from Figma inspect */}
          <div
            className="absolute inset-0 pointer-events-none z-[1]"
            style={{
              background:
                "linear-gradient(90deg, rgba(4, 16, 29, 1) 0%, rgba(4, 16, 29, 0.94) 34%, rgba(7, 22, 38, 0.35) 64%, rgba(8, 42, 89, 0) 100%)",
            }}
          />

          {/* Mobile dark overlay so text is readable over image on small screens */}
          <div
            className="absolute inset-0 pointer-events-none z-[1] sm:hidden"
            style={{
              background: "rgba(4, 16, 29, 0.75)",
            }}
          />

          {/* Content Container */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-12 xl:px-16">
            <div className="max-w-[620px] lg:max-w-[720px] flex flex-col items-start">
              {/* Headline */}
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-extrabold leading-[1.15] tracking-tight text-white whitespace-normal sm:whitespace-nowrap">
                <span>
                  Let’s Build a <span className="text-[#00BAEB]">Smarter Tomorrow.</span>
                </span>
              </h2>

              {/* Subtitle / Description */}
              <p className="mt-4 sm:mt-5 text-[14px] sm:text-[15px] leading-[23px] sm:leading-[25px] text-[#A0B3CC] max-w-[490px]">
                Talk to our team to see how SimpelERP can simplify your operations and support your growth.
              </p>

              {/* Action Button: Talk to Our Team */}
              <div className="mt-7 sm:mt-8">
                <button
                  onClick={openAgent}
                  className="group inline-flex items-center gap-2.5 rounded-full bg-white px-7 sm:px-8 py-3.5 text-[14px] sm:text-[14.5px] font-bold text-[#06152B] shadow-[0_0_24px_rgba(0,186,235,0.45)] hover:shadow-[0_0_36px_rgba(0,186,235,0.75)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer"
                >
                  <span>Talk to Our Team</span>
                  <ArrowRight
                    className="h-4 w-4 text-[#06152B] transition-transform duration-200 group-hover:translate-x-1"
                    strokeWidth={2.4}
                  />
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default SimpelErpPage;

